// Step 1 — Connect Client Layer
// A dedicated axios instance for synex-connect-server.
// Decoupled from the main application authentication lifecycle:
//   - Uses meeting-scoped participant JWTs when interacting with an active meeting.
//   - Isolates meeting 401/403 errors so they never destroy the main SynexOS application session.
//   - Automatically renews meeting sessions via meetingSessionService on 401 without global redirects.

import axios from "axios";
import { authService, isDefinitiveAuthError } from "../../../utils/auth";
import { getMeetingToken } from "../lib/meetingSession";
import { meetingSessionService } from "./meetingSessionService";
import { CONNECT_BASE } from "../config/connectConfig";
import logger from "../../../utils/logger";

export { CONNECT_BASE };

const connectClient = axios.create({
  baseURL: CONNECT_BASE,
});

/**
 * Extract meetingId from request configuration or active URL path.
 */
export function extractMeetingIdFromConfig(config) {
  if (config?.meetingId) return config.meetingId;

  // 1. Check URL path for /connect/meetings/:meetingId or /meetings/:meetingId
  const url = config?.url || "";
  const match = url.match(/(?:connect\/)?meetings\/([a-zA-Z0-9_-]+)/);
  if (match && match[1] && match[1] !== "resolve" && match[1] !== "presence" && match[1] !== "active") {
    return match[1];
  }

  // 2. Check request payload for meeting_id (e.g., token or presence requests)
  if (config?.data) {
    try {
      const data = typeof config.data === "string" ? JSON.parse(config.data) : config.data;
      if (data?.meeting_id) return data.meeting_id;
    } catch {
      /* ignore */
    }
  }

  // 3. Check current window path if inside /connect/:meetingId
  if (typeof window !== "undefined") {
    const pathMatch = window.location.pathname.match(/\/connect\/([a-zA-Z0-9_-]+)/);
    if (pathMatch && pathMatch[1] && pathMatch[1] !== "meetings" && pathMatch[1] !== "home") {
      return pathMatch[1];
    }
  }

  return null;
}

// Attach meeting-scoped token if available for this meeting, otherwise main-app token.
connectClient.interceptors.request.use(
  (config) => {
    // If Authorization is already explicitly set (e.g. by a caller with specific token), respect it
    const existingAuth = config.headers?.Authorization || config.headers?.authorization;
    if (existingAuth) {
      return config;
    }

    const meetingId = extractMeetingIdFromConfig(config);
    let token = null;
    let isMeetingScoped = false;

    // Explicit exception 1: join-session is the endpoint that creates/mints the meeting token from main application auth.
    // It must ALWAYS use the main application access token and NEVER attach a cached meeting-participant token.
    const url = config?.url || "";
    const isJoinSession = Boolean(
      url.includes("/join-session") || url.endsWith("join-session")
    );

    // Explicit exception 2: Caller explicitly requests skipping meeting token (e.g. pre-join metadata load)
    const isExplicitSkip = Boolean(config?.skipMeetingToken || config?.useMainToken);

    // Pre-join path detection: if on /connect/:meetingId and not /room, GET requests load pre-join metadata
    const isPreJoinPath =
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/connect/") &&
      !window.location.pathname.includes("/room");
    const isPreJoinMeetingGet =
      (config?.method?.toLowerCase() === "get" || !config?.method) &&
      isPreJoinPath &&
      !config?.forceMeetingToken;

    if (meetingId && !isJoinSession && !isExplicitSkip && !isPreJoinMeetingGet) {
      const meetingToken = getMeetingToken(meetingId);
      if (meetingToken) {
        token = meetingToken;
        isMeetingScoped = true;
        config._meetingId = meetingId;
        config._isMeetingScoped = true;
      }
    }

    // Fall back to main application access token (e.g. when creating meetings, join-session, or pre-join)
    if (!token) {
      token = authService.getToken();
    }

    if (token) {
      if (config.headers?.set) {
        config.headers.set("Authorization", `Bearer ${token}`);
      } else {
        config.headers = config.headers || {};
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Global 401 & 403 (Not authenticated) handling — isolates meeting participant session renewal from main-app auth
connectClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    const isAuthChallenge =
      status === 401 ||
      (status === 403 && typeof detail === "string" && detail.trim().toLowerCase() === "not authenticated");

    if (isAuthChallenge && originalRequest) {
      const meetingId = originalRequest._meetingId || extractMeetingIdFromConfig(originalRequest);
      const isMeetingScoped = Boolean(originalRequest._isMeetingScoped || (meetingId && getMeetingToken(meetingId)));

      // 1. MEETING-SCOPED 401: Renew meeting session without affecting main application
      if (isMeetingScoped && meetingId) {
        if (originalRequest._meetingRetry) {
          logger.warn("[Connect] Meeting session renewal failed or request retry failed for meeting:", meetingId);
          return Promise.reject(error);
        }

        originalRequest._meetingRetry = true;
        try {
          logger.info("[Connect] Attempting meeting session token renewal for meeting:", meetingId);
          const renewedSession = await meetingSessionService.refreshSession(meetingId);
          const newToken = renewedSession?.meeting_token;

          if (newToken) {
            if (originalRequest.headers?.set) {
              originalRequest.headers.set("Authorization", `Bearer ${newToken}`);
            } else {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            }
            return connectClient(originalRequest);
          }
        } catch (refreshErr) {
          logger.warn("[Connect] Meeting session token renewal rejected:", refreshErr?.message || refreshErr);
          // Never call authService.logout() or redirect to /auth/login for meeting session errors
          return Promise.reject(error);
        }
      }

      // 2. MAIN-APP GLOBAL 401 (Only when not a meeting-scoped request)
      const isAuthPage =
        typeof window !== "undefined" &&
        (window.location.pathname.includes("/auth/login") ||
          window.location.pathname.includes("/auth/signup"));

      if (isAuthPage || originalRequest._retry) {
        if (!isAuthPage && !authService.hasRefreshToken()) {
          logger.warn("[Connect] Main app 401 Unauthorized and no refresh token available.");
          // Only perform main-app logout if NOT inside an active meeting route
          const isConnectMeetingRoute =
            typeof window !== "undefined" && window.location.pathname.startsWith("/connect/");
          if (!isConnectMeetingRoute) {
            authService.logout();
            if (typeof window !== "undefined" && !window.location.pathname.includes("/auth/login")) {
              window.location.href = "/auth/login";
            }
          }
        }
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      let newToken;
      try {
        newToken = await authService.refreshToken();
      } catch (refreshErr) {
        const latestToken = authService.getToken();
        if (latestToken && !authService.isTokenExpired(latestToken)) {
          newToken = latestToken;
        } else if (isDefinitiveAuthError(refreshErr)) {
          logger.warn("[Connect] Definitive main app token refresh failure.");
          const isConnectMeetingRoute =
            typeof window !== "undefined" && window.location.pathname.startsWith("/connect/");
          if (!isConnectMeetingRoute) {
            authService.logout();
            if (typeof window !== "undefined" && !window.location.pathname.includes("/auth/login")) {
              window.location.href = "/auth/login";
            }
          }
          return Promise.reject(refreshErr);
        } else {
          return Promise.reject(refreshErr);
        }
      }

      if (originalRequest.headers?.set) {
        originalRequest.headers.set("Authorization", `Bearer ${newToken}`);
      } else {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
      }

      return connectClient(originalRequest);
    }
    return Promise.reject(error);
  }
);

export default connectClient;
